import { DateTime } from 'luxon';
import userSession from '@webapp-next/lib/user-session';
import useWebappStore from '@webapp-next/store';
import {
  AWARDS_TYPE_ENUM,
  EMPLOYMENT_TYPE,
} from '@/modules/people/definitions/pay-rates';
import useEmploymentStore from '@/modules/people/store/employment';
import useWorkingHoursStore from '@/modules/people/store/working-hours';
import useWorkDetailsStore from '@/modules/people/store/work-detail';
import {
  AGREED_HOURS_CALCULATION_TYPES,
  UNIT,
} from '@/modules/people/modules/leave/definitions-policy';
import checkFF from '@/lib/feature-flag';
import getCountryCodeForInstall from '@apps-hr/lib/request/utils/get-installation-country';

import { COUNTRY_CODE } from '../definitions';
import useRootStore from '@/modules/people/store/root';

export const DAY_MAPPER = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

// Luxon uses weekday numbers 1-7 (Mon-Sun)
const LUXON_WEEKDAY_MAPPER = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
};

export const agreedHoursOptions = {
  TOTAL_AGREED_HOURS: 'TOTAL_AGREED_HOURS',
  SPECIFIC_DAYS_TIMES: 'SPECIFIC_DAYS_TIMES',
};

export const getInstanceId = () => {
  const { InstanceId } = userSession();
  return InstanceId;
};

export function getDefaultAgreedHour(installId, employeeId) {
  return {
    installationId: installId,
    employeeId,
    threshold: 0,
    applyThreshold: false,
    workPeriod: {},
  };
}

export const defaultIndex = 0;

/**
 * Get the next occurrence of the given weekday in UTC (including today).
 *
 * @param {string} strDAY
 * @return {Date}
 */
export function getNextDateByDay(strDAY) {
  const targetWeekday = LUXON_WEEKDAY_MAPPER[strDAY];
  const today = DateTime.utc().startOf('day');

  if (!targetWeekday) {
    return today.toJSDate();
  }

  // 0..6 days forward (0 means today)
  const dayDiff = (targetWeekday - today.weekday + 7) % 7;
  return today.plus({ days: dayDiff }).toJSDate();
}

/**
 * Returns the weekday date for the current week, or the previous week if today
 * is before the requested weekday.
 *
 * Example (week start MONDAY):
 * - Fri 19 Dec -> Mon 15 Dec
 * - Sun 21 Dec -> Mon 15 Dec
 * - Mon 22 Dec -> Mon 22 Dec
 *
 * @param {string} strDAY
 * @return {Date}
 */
export function getCurrentOrPreviousDateByDay(strDAY) {
  const targetWeekday = LUXON_WEEKDAY_MAPPER[strDAY];
  const today = DateTime.utc().startOf('day');

  if (!targetWeekday) {
    return today.toJSDate();
  }

  // 0..6 days backwards (0 means today)
  const dayDiffBack = (today.weekday - targetWeekday + 7) % 7;
  return today.minus({ days: dayDiffBack }).toJSDate();
}

export function getDefaultWorkPeriod(strDAY) {
  strDAY = strDAY ? strDAY : 'MONDAY';
  const currentWeekStartDate = getCurrentOrPreviousDateByDay(strDAY);

  return {
    installationId: getInstanceId(),
    cycle: 'WEEKLY',
    dayOfWeek: strDAY,
    startDate: currentWeekStartDate.toISOString(),
    startCycleDate: currentWeekStartDate.toISOString(),
  };
}

// @dayWeek = 0 - 7 (Sun -Sat)
// @format = `EEE`, `EEEE` (Mon, Monday)
export const getDay = function(dayOfWeek, format) {
  return DateTime.local()
    .set({ weekday: dayOfWeek })
    .toFormat(format);
};

/**
 * Get visibility of the 'Pay overtime' checkbox
 * @returns {boolean}
 */

export function getCheckBoxVisibility() {
  const rootState = useRootStore();
  const webappState = useWebappStore();
  const employmentState = useEmploymentStore();
  const countryCode =
    webappState.setup.PortfolioCountryCode ||
    getCountryCodeForInstall()?.toUpperCase();

  // TODO: logic for this might change once we have multiple pay details
  const localPayDetail = employmentState.getLocalPayDetailByIndex(defaultIndex);
  const payRateType = localPayDetail?.payRateType;
  const isCustomTerm = payRateType === AWARDS_TYPE_ENUM.CUSTOM_TERM.Code;
  const isLibraryAward =
    payRateType === AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code;
  const isAustralia = countryCode === COUNTRY_CODE.AU;
  const isHourlyTimeHalfOvertime =
    payRateType === AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code;
  const isPartTimeEmployment =
    localPayDetail?.type === EMPLOYMENT_TYPE.PART_TIME;

  // TODO: Find a better way to determine if this is a PRE contract or not
  const isAnyPREContractSelected = employmentState
    .getLocalPayDetails()
    .map(payDetail =>
      rootState.setup.EmploymentTermArray.find(
        term => term.Id === payDetail.contract
      )
    )
    .some(
      contract =>
        typeof contract?.Id === 'number' &&
        contract?.IsPayPolicy === true &&
        !!contract.IsTemplate === false
    );
  const isShowingCheckboxForAllPREContracts = checkFF(
    'pre-4624-show-override-threshold-checkbox-for-all-pps'
  );

  return (
    (isPartTimeEmployment &&
      ((isAustralia && isLibraryAward) || isCustomTerm)) ||
    isHourlyTimeHalfOvertime ||
    (isAnyPREContractSelected && isShowingCheckboxForAllPREContracts)
  );
}

export function getLeaveRuleAssignmentValidationMessage(
  item,
  agreedHoursPoliciesByLeaveRuleId,
  policiesByLeaveRuleId,
  t
) {
  if (!policiesByLeaveRuleId || !policiesByLeaveRuleId[item.link]) {
    return null;
  }

  const policy = policiesByLeaveRuleId[item.link];

  const workingHoursState = useWorkingHoursStore();
  const workDetailsState = useWorkDetailsStore();

  const errorMessages = [];

  if (!workingHoursState?.hireDate && !workDetailsState.hiredOn) {
    if (policy.tenureMilestones.length > 1) {
      errorMessages.push(t('people:people-multi-selector:error-hire-date'));
    } else {
      const resetRollOver =
        policy.tenureMilestones[0]?.limits?.balanceRollover?.resetRollover;
      const fullRollOver =
        policy.tenureMilestones[0]?.limits?.balanceRollover?.fullRollover;
      const fixedReleaseAccural =
        policy.tenureMilestones[0]?.rules[0]?.accrualRate?.fixedReleaseAccrual;
      const agreedHoursFixedAccrual =
        policy.tenureMilestones[0]?.rules[0]?.accrualRate
          .agreedHoursFixedAccrual;

      if (
        resetRollOver?.shouldUseEmployeeHireDate ||
        fullRollOver?.shouldUseEmployeeHireDate ||
        fixedReleaseAccural?.shouldUseEmployeeHireDate ||
        agreedHoursFixedAccrual?.shouldUseEmployeeHireDate
      ) {
        errorMessages.push(t('people:people-multi-selector:error-hire-date'));
      }
    }
  }

  if (!agreedHoursPoliciesByLeaveRuleId[item.link]) {
    return errorMessages;
  }

  if (
    workingHoursState?.selectedOption === agreedHoursOptions.TOTAL_AGREED_HOURS
  ) {
    if (workingHoursState.agreedHour?.threshold === 0) {
      errorMessages.push(
        t('people:people-multi-selector:error-set-total-hours')
      );
    }

    const workingDaysPerCycle =
      Number(workingHoursState.agreedHour?.workingDaysPerCycle) || 0;

    const policy = agreedHoursPoliciesByLeaveRuleId[item.link];
    const ignoreWorkingDaysPerCycle =
      policy.tenureMilestones[0]?.rules[0]?.accrualRate.agreedHoursFixedAccrual
        .calculationType === AGREED_HOURS_CALCULATION_TYPES.WEEKS &&
      policy.defaultUnit === UNIT.UNIT_HOUR;

    if (!workingDaysPerCycle && !ignoreWorkingDaysPerCycle) {
      errorMessages.push(
        t('people:people-multi-selector:error-set-days-worked-per-period')
      );
    }
  }

  if (
    workingHoursState?.selectedOption === agreedHoursOptions.SPECIFIC_DAYS_TIMES
  ) {
    const regularWorkingHours =
      Number(workingHoursState?.agreedDaysTimesThreshold) || 0;
    if (!regularWorkingHours) {
      errorMessages.push(
        t('people:people-multi-selector:error-set-total-hours')
      );
    }
  }

  return errorMessages;
}
